/**
 * The URL scheme, which IS the object graph made navigable (design §2.8).
 *
 *   /                             DECK
 *   /c/<post_id>[#band]           the case, deep-link target of every push
 *   /o/<trade_id>                 order forensics
 *   /cell/<source>/<instr>/<dir>  one cell
 *   /fault/<class>/<anchor_id>    one fault
 *   /conditions /gates /ledger /cells /faults /admin
 *
 * Every push message, every row and every fault resolves to one of these,
 * and **no push ever links to a home page**.
 *
 * `post_id` may embed a URL with slashes (axios), so it travels
 * percent-encoded in exactly one path segment. Hand-rolled rather than
 * react-router: the whole surface is six routes and a dependency that owns
 * navigation is a dependency that owns the back button.
 */

import { useEffect, useState } from "react";

export const BASE_PATH = "/console";

export type Route =
  | { name: "deck" }
  | { name: "case"; postId: string; band?: string }
  | { name: "order"; tradeId: string }
  | { name: "cell"; source: string; instrument: string; direction: string }
  | { name: "fault"; cls: string; anchorId: string }
  | { name: "conditions" }
  | { name: "gates" }
  | { name: "ledger" }
  | { name: "cells" }
  | { name: "faults" }
  | { name: "admin" };

export function parseRoute(pathname: string, hash = ""): Route {
  let p = pathname;
  if (p.startsWith(BASE_PATH)) p = p.slice(BASE_PATH.length);
  if (!p.startsWith("/")) p = `/${p}`;
  const seg = p.split("/").filter(Boolean).map(decodeURIComponent);
  const band = hash.replace(/^#/, "") || undefined;

  if (seg.length === 0) return { name: "deck" };
  switch (seg[0]) {
    case "c":
      return seg[1] ? { name: "case", postId: seg[1], band } : { name: "deck" };
    case "o":
      return seg[1] ? { name: "order", tradeId: seg[1] } : { name: "ledger" };
    case "cell":
      return seg[3]
        ? { name: "cell", source: seg[1]!, instrument: seg[2]!, direction: seg[3]! }
        : { name: "cells" };
    case "fault":
      return seg[2] ? { name: "fault", cls: seg[1]!, anchorId: seg[2]! } : { name: "faults" };
    case "conditions":
      return { name: "conditions" };
    case "gates":
      return { name: "gates" };
    case "ledger":
      return { name: "ledger" };
    case "cells":
      return { name: "cells" };
    case "faults":
      return { name: "faults" };
    case "admin":
      return { name: "admin" };
    default:
      return { name: "deck" };
  }
}

/** The query string as it stands, `?…` included, or "". */
function currentSearch(): string {
  return typeof location === "undefined" ? "" : location.search;
}

/**
 * THE QUERY STRING SURVIVES NAVIGATION.
 *
 * The time bracket and the deck's source filter live in the URL so a range
 * can be linked and reloaded. Dropping the search on every nav click would
 * silently reset the reader's range each time they moved between DECK and
 * LEDGER — a state the operator never chose, arriving without a sound. The
 * path and the hash are the route; the query is the reading.
 */
export function href(r: Route): string {
  const e = encodeURIComponent;
  const q = currentSearch();
  switch (r.name) {
    case "deck":
      return `${BASE_PATH}/${q}`;
    case "case":
      return `${BASE_PATH}/c/${e(r.postId)}${q}${r.band ? `#${r.band}` : ""}`;
    case "order":
      return `${BASE_PATH}/o/${e(r.tradeId)}${q}`;
    case "cell":
      return `${BASE_PATH}/cell/${e(r.source)}/${e(r.instrument)}/${e(r.direction)}${q}`;
    case "fault":
      return `${BASE_PATH}/fault/${e(r.cls)}/${e(r.anchorId)}${q}`;
    default:
      return `${BASE_PATH}/${r.name}${q}`;
  }
}

export function navigate(r: Route): void {
  if (typeof history === "undefined") return;
  history.pushState(null, "", href(r));
  dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Write query parameters in place. `null` removes a key.
 *
 * `replaceState`, never `pushState`: changing the range is not a back-button
 * step. Fifteen clicks through the presets must not cost fifteen presses of
 * Back to leave the surface.
 */
export function setParams(patch: Record<string, string | null | undefined>): void {
  if (typeof location === "undefined" || typeof history === "undefined") return;
  const sp = new URLSearchParams(location.search);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || v === "") sp.delete(k);
    else sp.set(k, v);
  }
  const q = sp.toString();
  history.replaceState(history.state, "", `${location.pathname}${q ? `?${q}` : ""}${location.hash}`);
}

/** One parameter, or null. Reads the live URL — the store owns the state, this
 *  is only the first-paint seed. */
export function getParam(name: string): string | null {
  if (typeof location === "undefined") return null;
  return new URLSearchParams(location.search).get(name);
}

/** The ONE viewport query in the product.
 *
 *  It decides whether a component is MOUNTED, never whether a mounted
 *  component is visible. That distinction is the whole lesson of the old
 *  dashboard: a CSS-hidden branch still builds its DOM, still runs its
 *  effects and still polls. There is no `display: none` on a list anywhere
 *  in `web/`. */
export function useWideViewport(minPx = 1100): boolean {
  const query = `(min-width: ${minPx}px)`;
  const read = () => (typeof matchMedia === "function" ? matchMedia(query).matches : false);
  const [wide, setWide] = useState<boolean>(read);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(query);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [query]);
  return wide;
}

export function useRoute(): [Route, (r: Route) => void] {
  const read = () =>
    typeof location === "undefined"
      ? ({ name: "deck" } as Route)
      : parseRoute(location.pathname, location.hash);
  const [route, setRoute] = useState<Route>(read);
  useEffect(() => {
    const on = () => setRoute(read());
    addEventListener("popstate", on);
    addEventListener("hashchange", on);
    return () => {
      removeEventListener("popstate", on);
      removeEventListener("hashchange", on);
    };
  }, []);
  return [route, navigate];
}
